import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Select, Space } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { errorText, listDocuments, openPortalFile } from "./api";
import { formatDay } from "../../../serviceProvider/components/workReports/format";
import { documentKind } from "./labels";
import "./portal.css";

const fileNameOf = doc => (/\.pdf$/i.test(doc.title) ? doc.title : `${doc.title}.pdf`);

/** Documents the company handed over: signed reports, protocols, sent diaries and reports. */
const PortalDocuments = () => {
  const { t } = useTranslation("dashboard");
  const [state, setState] = useState({ loading: true, list: [] });
  const [project, setProject] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    listDocuments()
      .then(res => setState({ loading: false, list: res.data.data }))
      .catch(err =>
        setState({
          loading: false,
          list: [],
          error: errorText(err, t("The documents could not be loaded."))
        })
      );
  }, [t]);

  const projects = useMemo(() => {
    const map = new Map();
    state.list.forEach(d => d.project && map.set(String(d.project._id), d.project.name));
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [state.list]);

  const shown =
    project === "all"
      ? state.list
      : state.list.filter(d => String(d.project?._id || "none") === project);

  const open = async (doc, download) => {
    setError("");
    try {
      await openPortalFile(`/documents/${doc.kind}/${doc._id}`, fileNameOf(doc), { download });
    } catch (err) {
      setError(errorText(err, t("The file is not available.")));
    }
  };

  const columns = [
    { title: t("Document"), dataIndex: "title", mobile: "title" },
    {
      title: t("Type"),
      dataIndex: "kind",
      mobile: "subtitle",
      render: value => documentKind(t, value)
    },
    { title: t("Project"), dataIndex: ["project", "name"], render: value => value || "–" },
    { title: t("Date"), dataIndex: "date", render: value => formatDay(value) },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, doc) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => open(doc, false)}>
            {t("Open")}
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => open(doc, true)}
            aria-label={t("Download")}
          />
        </Space>
      )
    }
  ];

  return (
    <div className="portal-page">
      {(state.error || error) && (
        <Alert className="portal-gap" type="error" showIcon message={state.error || error} />
      )}
      <Card
        title={t("Documents")}
        className="portal-card"
        extra={
          projects.length > 1 ? (
            <Select
              className="portal-select"
              value={project}
              onChange={setProject}
              options={[{ value: "all", label: t("All projects") }, ...projects]}
              aria-label={t("Project")}
            />
          ) : null
        }
      >
        <ResponsiveTable
          rowKey={doc => `${doc.kind}:${doc._id}`}
          loading={state.loading}
          columns={columns}
          dataSource={shown}
          pagination={shown.length > 20 ? { pageSize: 20 } : false}
          mobileAvatar={false}
          locale={{ emptyText: t("No documents yet.") }}
        />
      </Card>
    </div>
  );
};

export default PortalDocuments;
